import React, { useState } from 'react';
import APP_COLOURS from '../../config/colors';

const AppUpdateGuide = () => {
  const [currentStep, setCurrentStep] = useState(0);
  const [expandedStep, setExpandedStep] = useState(null);

  const steps = [
    {
      id: 1,
      title: 'Exit ScaleFusion',
      description: 'Exit the TRAQ app and press the button on the top right corner of the screen.',
      subSteps: [
        'Open the TRAQ app if not already open',
        'Press the exit button located in the top right corner',
        'If prompted for a password, enter: 2024',
        'You will be taken to the device home screen'
      ],
      icon: 'fa-sign-out-alt',
      color: APP_COLOURS.BLUE
    },
    {
      id: 2,
      title: 'Uninstall Existing TRAQ Apps',
      description: 'Remove all current TRAQ app installations from the device.',
      subSteps: [
        'On the home screen, locate all TRAQ app icons',
        'Long press on the TRAQ app icon',
        'Select "Uninstall" or drag to the uninstall area',
        'Confirm the uninstallation',
        'Repeat for ALL TRAQ apps installed on the device'
      ],
      icon: 'fa-trash-alt',
      color: APP_COLOURS.RED
    },
    {
      id: 3,
      title: 'Open Gmail',
      description: 'Navigate to Gmail to find the app update email.',
      subSteps: [
        'Open the Gmail app on the device',
        'Look for the latest email containing the TRAQ app update link',
        'The email subject should reference "TRAQ App Update" or similar'
      ],
      icon: 'fa-envelope',
      color: APP_COLOURS.ORANGE
    },
    {
      id: 4,
      title: 'Download the New App',
      description: 'Click the download link and get the latest TRAQ app.',
      subSteps: [
        'Click on the download link in the email',
        'The link should open in Chrome browser',
        'Press the "Download" button',
        'If you see a warning, press "Download anyway" to continue',
        'Wait for the download to complete'
      ],
      icon: 'fa-download',
      color: APP_COLOURS.GREEN
    },
    {
      id: 5,
      title: 'Install the App',
      description: 'Open the downloaded file and install the TRAQ app.',
      subSteps: [
        'Navigate to your Downloads folder',
        'Tap on the downloaded APK file',
        'If prompted to "Allow install from unknown sources", enable it',
        'When prompted to scan the app, select "Scan" to verify safety',
        'Tap "Install" to begin installation',
        'Wait for the installation to complete'
      ],
      icon: 'fa-mobile-alt',
      color: APP_COLOURS.PURPLE
    },
    {
      id: 6,
      title: 'Pin the App (Kiosk Mode)',
      description: 'Set up the app in kiosk mode so it stays fixed on the device.',
      subSteps: [
        'Open the newly installed TRAQ app',
        'Press the menu button (three vertical lines |||) on the bottom left of the screen',
        'Tap on the TRAQ app icon in the recent apps view',
        'From the popup menu, select "PIN APP"',
        'The app will now be locked in kiosk mode',
        'The device is now ready for use'
      ],
      icon: 'fa-thumbtack',
      color: APP_COLOURS.BLUE2
    }
  ];

  const handleStepClick = (stepIndex) => {
    setExpandedStep(expandedStep === stepIndex ? null : stepIndex);
    setCurrentStep(stepIndex);
  };

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
      setExpandedStep(currentStep + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
      setExpandedStep(currentStep - 1);
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: APP_COLOURS.BG2,
      padding: '20px'
    }}>
      <div style={{ maxWidth: '900px', margin: '0 auto' }}>
        {/* Header */}
        <div style={{
          backgroundColor: APP_COLOURS.CARD,
          borderRadius: '16px',
          padding: '30px',
          marginBottom: '20px',
          border: `1px solid ${APP_COLOURS.BLUE}20`,
        }}>
          <div style={{ textAlign: 'center' }}>
            <div style={{
              width: '80px',
              height: '80px',
              borderRadius: '20px',
              background: `linear-gradient(135deg, ${APP_COLOURS.BLUE}, ${APP_COLOURS.PURPLE})`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              margin: '0 auto 20px'
            }}>
              <i className="fas fa-mobile-alt" style={{ color: '#fff', fontSize: '36px' }}></i>
            </div>
            <h1 style={{
              color: APP_COLOURS.TEXTCOLOR,
              fontSize: '32px',
              fontWeight: 'bold',
              margin: '0 0 10px 0'
            }}>
              TRAQ App Update Guide
            </h1>
            <p style={{
              color: APP_COLOURS.TEXTCOLOR,
              opacity: 0.7,
              margin: 0,
              fontSize: '16px',
              maxWidth: '600px',
              marginLeft: 'auto',
              marginRight: 'auto'
            }}>
              Follow these steps to update the TRAQ app on your allocated device. This process will take approximately 5-10 minutes.
            </p>
          </div>

          {/* Progress Bar */}
          <div style={{ marginTop: '30px' }}>
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginBottom: '10px'
            }}>
              <span style={{ color: APP_COLOURS.TEXTCOLOR, fontSize: '14px' }}>
                Step {currentStep + 1} of {steps.length}
              </span>
              <span style={{ color: APP_COLOURS.BLUE, fontSize: '14px', fontWeight: '600' }}>
                {Math.round(((currentStep + 1) / steps.length) * 100)}% Complete
              </span>
            </div>
            <div style={{
              height: '8px',
              backgroundColor: APP_COLOURS.BG3,
              borderRadius: '4px',
              overflow: 'hidden'
            }}>
              <div style={{
                height: '100%',
                width: `${((currentStep + 1) / steps.length) * 100}%`,
                background: `linear-gradient(90deg, ${APP_COLOURS.BLUE}, ${APP_COLOURS.PURPLE})`,
                borderRadius: '4px',
                transition: 'width 0.3s ease'
              }}></div>
            </div>
          </div>
        </div>

        {/* Quick Navigation */}
        <div style={{
          backgroundColor: APP_COLOURS.CARD,
          borderRadius: '16px',
          padding: '20px',
          marginBottom: '20px',
          border: `1px solid ${APP_COLOURS.BLUE}10`,
        }}>
          <h3 style={{
            color: APP_COLOURS.TEXTCOLOR,
            fontSize: '14px',
            fontWeight: '600',
            margin: '0 0 15px 0',
            opacity: 0.7
          }}>
            QUICK NAVIGATION
          </h3>
          <div style={{
            display: 'flex',
            gap: '10px',
            flexWrap: 'wrap'
          }}>
            {steps.map((step, index) => (
              <button
                key={step.id}
                onClick={() => handleStepClick(index)}
                style={{
                  width: '40px',
                  height: '40px',
                  borderRadius: '10px',
                  border: 'none',
                  backgroundColor: currentStep === index ? step.color : APP_COLOURS.BG3,
                  color: currentStep === index ? '#fff' : APP_COLOURS.TEXTCOLOR,
                  fontSize: '14px',
                  fontWeight: '600',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}
              >
                {index + 1}
              </button>
            ))}
          </div>
        </div>

        {/* Steps List */}
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          gap: '12px'
        }}>
          {steps.map((step, index) => (
            <div
              key={step.id}
              style={{
                backgroundColor: APP_COLOURS.CARD,
                borderRadius: '16px',
                border: `1px solid ${currentStep === index ? step.color + '40' : APP_COLOURS.BG4}`,
                overflow: 'hidden',
                transition: 'all 0.3s ease'
              }}
            >
              {/* Step Header */}
              <div
                onClick={() => handleStepClick(index)}
                style={{
                  padding: '20px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '15px',
                  cursor: 'pointer',
                  backgroundColor: currentStep === index ? `${step.color}10` : 'transparent'
                }}
              >
                {/* Step Number */}
                <div style={{
                  width: '50px',
                  height: '50px',
                  borderRadius: '12px',
                  backgroundColor: currentStep === index ? step.color : APP_COLOURS.BG3,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                  transition: 'all 0.3s ease'
                }}>
                  <i 
                    className={`fas ${step.icon}`} 
                    style={{ 
                      color: currentStep === index ? '#fff' : step.color, 
                      fontSize: '20px' 
                    }}
                  ></i>
                </div>

                {/* Step Info */}
                <div style={{ flex: 1 }}>
                  <div style={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '10px',
                    marginBottom: '4px'
                  }}>
                    <span style={{
                      fontSize: '12px',
                      fontWeight: '600',
                      color: step.color,
                      textTransform: 'uppercase',
                      letterSpacing: '1px'
                    }}>
                      Step {step.id}
                    </span>
                    {index < currentStep && (
                      <span style={{
                        backgroundColor: APP_COLOURS.GREEN,
                        color: '#fff',
                        padding: '2px 8px',
                        borderRadius: '10px',
                        fontSize: '10px',
                        fontWeight: '600'
                      }}>
                        COMPLETED
                      </span>
                    )}
                  </div>
                  <h3 style={{
                    color: APP_COLOURS.TEXTCOLOR,
                    fontSize: '18px',
                    fontWeight: '600',
                    margin: 0
                  }}>
                    {step.title}
                  </h3>
                  <p style={{
                    color: APP_COLOURS.TEXTCOLOR,
                    opacity: 0.6,
                    fontSize: '14px',
                    margin: '5px 0 0 0'
                  }}>
                    {step.description}
                  </p>
                </div>

                {/* Expand Icon */}
                <div style={{
                  width: '30px',
                  height: '30px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <i 
                    className={`fas ${expandedStep === index ? 'fa-chevron-up' : 'fa-chevron-down'}`}
                    style={{ 
                      color: APP_COLOURS.TEXTCOLOR, 
                      opacity: 0.5,
                      transition: 'transform 0.3s ease'
                    }}
                  ></i>
                </div>
              </div>

              {/* Expanded Content */}
              {expandedStep === index && (
                <div style={{
                  padding: '0 20px 20px 85px',
                  borderTop: `1px solid ${APP_COLOURS.BG4}`
                }}>
                  <div style={{ paddingTop: '20px' }}>
                    <h4 style={{
                      color: APP_COLOURS.TEXTCOLOR,
                      fontSize: '14px',
                      fontWeight: '600',
                      margin: '0 0 15px 0'
                    }}>
                      Detailed Instructions:
                    </h4>
                    <ol style={{
                      margin: 0,
                      paddingLeft: '20px',
                      color: APP_COLOURS.TEXTCOLOR
                    }}>
                      {step.subSteps.map((subStep, subIndex) => (
                        <li key={subIndex} style={{
                          marginBottom: '12px',
                          fontSize: '15px',
                          lineHeight: '1.5',
                          opacity: 0.85
                        }}>
                          {subStep}
                        </li>
                      ))}
                    </ol>

                    {/* Image Placeholder */}
                    <div style={{
                      marginTop: '20px',
                      backgroundColor: APP_COLOURS.BG3,
                      borderRadius: '12px',
                      border: `2px dashed ${APP_COLOURS.BG5}`,
                      padding: '30px',
                      textAlign: 'center'
                    }}>
                      <i 
                        className="fas fa-image" 
                        style={{ 
                          color: APP_COLOURS.TEXTCOLOR, 
                          fontSize: '32px',
                          opacity: 0.3,
                          marginBottom: '10px',
                          display: 'block'
                        }}
                      ></i>
                      <p style={{
                        color: APP_COLOURS.TEXTCOLOR,
                        opacity: 0.5,
                        fontSize: '14px',
                        margin: 0
                      }}>
                        Reference image for Step {step.id}
                      </p>
                      <p style={{
                        color: APP_COLOURS.TEXTCOLOR,
                        opacity: 0.3,
                        fontSize: '12px',
                        margin: '5px 0 0 0'
                      }}>
                        Image can be added to: /src/components/AppUpdateGuide/images/step{step.id}.png
                      </p>
                    </div>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Navigation Buttons */}
        <div style={{
          backgroundColor: APP_COLOURS.CARD,
          borderRadius: '16px',
          padding: '20px',
          marginTop: '20px',
          border: `1px solid ${APP_COLOURS.BLUE}10`,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: '15px'
        }}>
          <button
            onClick={handlePrevious}
            disabled={currentStep === 0}
            style={{
              padding: '12px 24px',
              borderRadius: '10px',
              border: `1px solid ${APP_COLOURS.BG5}`,
              backgroundColor: currentStep === 0 ? APP_COLOURS.BG3 : 'transparent',
              color: APP_COLOURS.TEXTCOLOR,
              fontSize: '14px',
              fontWeight: '600',
              cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
              opacity: currentStep === 0 ? 0.5 : 1,
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            <i className="fas fa-arrow-left"></i>
            Previous Step
          </button>

          <button
            onClick={handleNext}
            disabled={currentStep === steps.length - 1}
            style={{
              padding: '12px 24px',
              borderRadius: '10px',
              border: 'none',
              backgroundColor: currentStep === steps.length - 1 ? APP_COLOURS.GREEN : APP_COLOURS.BLUE,
              color: '#fff',
              fontSize: '14px',
              fontWeight: '600',
              cursor: currentStep === steps.length - 1 ? 'default' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: '8px'
            }}
          >
            {currentStep === steps.length - 1 ? (
              <>
                <i className="fas fa-check"></i>
                All Steps Complete
              </>
            ) : (
              <>
                Next Step
                <i className="fas fa-arrow-right"></i>
              </>
            )}
          </button>
        </div>

        {/* Important Notes */}
        <div style={{
          backgroundColor: `${APP_COLOURS.ORANGE}15`,
          borderRadius: '16px',
          padding: '20px',
          marginTop: '20px',
          border: `1px solid ${APP_COLOURS.ORANGE}30`,
        }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', gap: '15px' }}>
            <div style={{
              width: '40px',
              height: '40px',
              borderRadius: '10px',
              backgroundColor: APP_COLOURS.ORANGE,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0
            }}>
              <i className="fas fa-exclamation-triangle" style={{ color: '#000', fontSize: '18px' }}></i>
            </div>
            <div>
              <h3 style={{
                color: APP_COLOURS.TEXTCOLOR,
                fontSize: '16px',
                fontWeight: '600',
                margin: '0 0 10px 0'
              }}>
                Important Notes
              </h3>
              <ul style={{
                margin: 0,
                paddingLeft: '20px',
                color: APP_COLOURS.TEXTCOLOR,
                opacity: 0.85
              }}>
                <li style={{ marginBottom: '8px', fontSize: '14px' }}>
                  The ScaleFusion password is: <strong style={{ color: APP_COLOURS.BLUE }}>2024</strong>
                </li>
                <li style={{ marginBottom: '8px', fontSize: '14px' }}>
                  Make sure to uninstall ALL existing TRAQ apps before installing the new version
                </li>
                <li style={{ marginBottom: '8px', fontSize: '14px' }}>
                  Ensure you have a stable internet connection during the download
                </li>
                <li style={{ marginBottom: '8px', fontSize: '14px' }}>
                  If you encounter any issues, contact your manager for assistance
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Footer */}
        <div style={{
          textAlign: 'center',
          padding: '30px 20px',
          color: APP_COLOURS.TEXTCOLOR,
          opacity: 0.5,
          fontSize: '13px'
        }}>
          <p style={{ margin: 0 }}>TRAQ App Update Guide v1.0</p>
          <p style={{ margin: '5px 0 0 0' }}>For technical support, please contact your area manager</p>
        </div>
      </div>
    </div>
  );
};

export default AppUpdateGuide;
